class AbstractComponent extends EventHandler {

  constructor(args) {
    if (new.target === AbstractComponent)
      throw "AbstractComponent is abstract. Do not initialize it directly.";

    super();

    this.iframable = args.iframable;
    this.name = args.name;
    this.responsiveChildren = args.responsiveChildren;

    this._options = {};

    this._element = null;
    this.app = null;
    this._building = false;

    this._isIframe = false;
    this._fakeSize = false;

  }

  css() { throw "You must override this method"; }
  html(props) { throw "You must override this method"; }
  forcedWidth() { }
  forcedHeight() { }
  initEventListeners() { }



  attachSlaask(slaask) {
    this.app = slaask;
  }

  /**
   * @param  {{props: Object, inIframe: boolean, externalCss: boolean, additionalCss: string, customCss: string}} options
   */
  preBuildElement(options) {
    Utils.feedIfEmpty(options, {
      props: {},
      inIframe: false,
      externalCss: false,
      additionalCss: null,
      customCss: null
    });
    this._options = options;
  }

  async buildIfNeeded() {
    if (!this.element) {
      if (this._building) await this.when('ready');
      else await this.buildElement(this._options);
    }
  }

  isReady() {
    return this.didEventHappen('ready');
  }

  async buildElement(opt) {
    if (this.element) throw "Element already built.";

    this._building = true;

    if (opt.inIframe && this.iframable)
      await this._buildWithIframe.apply(this, arguments);
    else
      await this._buildWithoutIframe.apply(this, arguments);

    this.initEventListeners();

    this._eventHappened('ready');
    this._building = false;
  }

  _applyOffsets() {
    if(this.app.getScreenWidth() > this.app._mobile_threshold) {
      if(this.app._x_offset !== 0) {
        if (this.app._widget_left) {
          this.container.style.left    = (this.app._x_offset) + 'px';
        }else {
          this.container.style.right    = (this.app._x_offset) + 'px';
        }
      }
  
      if (this.app._y_offset !== 0) {
        this.container.style.bottom    += (this.app._y_offset) + 'px';
      }
    }
  }

  async _buildWithIframe(opt) {
    this._isIframe = true;

    let iframeContainer = document.createElement('div');
    iframeContainer.id = this.id;
    iframeContainer.className = "slaask-hidden ";

    let iframe = document.createElement('iframe');
    iframe.className = "slaask-iframe";
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('allow', 'camera *; microphone *; display-capture *; clipboard-write');
    await new Promise((resolve, reject) => {
      iframe.addEventListener('load', () => { resolve() });
      iframeContainer.appendChild(iframe);
      Dom.parent.document.body.appendChild(iframeContainer);
    });

    if (opt.externalCss) await this._loadExternalCss(iframe.contentWindow.document, opt.additionalCss);
    else await this._loadInternalCss(iframe.contentWindow.document, this.css(), opt.additionalCss);
    if (opt.customCss) this._loadInternalCss(iframe.contentWindow.document, opt.customCss);

    iframe.contentWindow.document.body.className = "slaask-iframe";
    iframe.contentWindow.document.body.innerHTML = this.html(opt.props);
    iframe.contentWindow.document.head.appendChild(this._getBaseTag());

    Object.defineProperty(iframe.contentWindow, '_slaask', {
      get: () => this.app,
      set: function(newValue) { }
    });

    Object.defineProperty(iframe.contentWindow, '_xeno', {
      get: () => this.app,
      set: function(newValue) { }
    });

    this._iframePolyfills(iframe.contentWindow);

    this._element = iframe.contentWindow.document.body.children[0];
    this._element.id = this.id;
    Utils.addClass(this._element, this.id);
    iframeContainer.className = "slaask-iframe-container slaask-component " + this._element.className;
    this._registerEvent(this.window, 'resize', this.matchContent);
  }

  async _buildWithoutIframe(opt) {
    // No additionnalCss outside iframes
    this._isIframe = false;

    if (opt.externalCss) await this._loadExternalCss(Dom.parent.document);
    else await this._loadInternalCss(Dom.parent.document, this.css());
    if (opt.customCss) this._loadInternalCss(Dom.parent.document, opt.customCss);

    let container = document.createElement('div');
    container.innerHTML = this.html(opt.props);
    this._element = container.children[0];
    this._element.id = this.id;
    Utils.addClass(this._element, this.id);
    Utils.addClass(this._element, "slaask-component");
    Dom.parent.document.body.appendChild(this._element);
  }

  async _loadExternalCss(doc, additionnalFiles) {
    let promises = [];
    additionnalFiles = Utils.ensureArray(additionnalFiles);
    Utils.forEach(additionnalFiles, fileName => {
      promises.push(Utils.loadCssFile(doc, Dom.getCssUrl(fileName)));
    });
    promises.push(Utils.loadCssFile(doc, Dom.getCssUrl(this.name)));
    return Promise.all(promises);
  }

  async _loadInternalCss(doc, css, additionalCss = "") {
    await Utils.appendCss(doc, additionalCss + css);
  }

  _iframePolyfills(iframeWindow) {
    if (iframeWindow.NodeList && !iframeWindow.NodeList.prototype.forEach) {
      iframeWindow.NodeList.prototype.forEach = iframeWindow.Array.prototype.forEach;
    }
  }

  _getBaseTag() {
    let tag = document.createElement('base');
    tag.setAttribute("target", "_parent");
    return tag;
  }

  /* SHORTCUTS */

  querySelector() {
    if (!this._element) return null;
    else {
      return this._element.querySelector.apply(this._element, arguments);
    }
  }
  get find() { return this.querySelector; }

  querySelectorAll() {
    if (!this._element) return null;
    else {
      return this._element.querySelectorAll.apply(this._element, arguments);
    }
  }
  get $() { return this.querySelectorAll; }
  get findAll() { return this.querySelectorAll; }

  getElementById() {
    if (!this._element) return null;
    else {
      return this.document.getElementById.apply(this.document, arguments);
    }
  }
  get findById() { return this.getElementById; }

  /**
   * addClass
   *
   * addClass(className)
   * addClass(selector, className)
   * addClass(null, className)
   *
   * @param {string|Element} selector  CSS selector or HTML element where to add the class
   * @param {string} className         Name of the class to add
   */
  addClass(selector, className) {
    if (!Utils.defined(className) || !selector) {
      if (selector) className = selector;
      Utils.addClass([this.element, this.container], className);
    }
    else if (typeof selector != "string") {
      Utils.addClass(selector, className);
    }
    else {
      let selectedElements = this.querySelectorAll(selector);
      Utils.addClass(selectedElements, className);
    }
  }

  /**
   * removeClass
   *
   * removeClass(className)
   * removeClass(selector, className)
   * removeClass(null, className)
   *
   * @param {string|Element} selector  CSS selector or HTML element where to remove the class
   * @param {string} className         Name of the class to remove
   */
  removeClass(selector, className) {
    if (!Utils.defined(className) || !selector) {
      if (selector) className = selector;
      Utils.removeClass([this.element, this.container], className);
    }
    else if (typeof selector != "string") {
      Utils.removeClass(selector, className);
    }
    else {
      let selectedElements = this.querySelectorAll(selector);
      Utils.removeClass(selectedElements, className);
    }
  }

  hasClass(selector, className) {
    if (!Utils.defined(className) || !selector) {
      if (selector) className = selector;
      return Utils.hasClass(this.container, className);
    }
    else if (typeof selector != "string") {
      return Utils.hasClass(selector, className);
    }
    else {
      let selectedElement = this.querySelector(selector);
      return Utils.hasClass(selectedElement, className);
    }
  }


  fakeSize(w, h) {
    if (this._isIframe) {
      this._fakeSize = true;
      if (Utils.exists(w))  {
        if (w == "current") w = Dom.parent.getComputedStyle(this.container).width.replace('px', '')
        this.container.style.width                   = w + 'px';
        this.document.body.style.width               = w + 'px';
        this.document.head.parentElement.style.width = w + 'px';
      }
      if (Utils.exists(h)) {
        if (h == "current") h = Dom.parent.getComputedStyle(this.container).height.replace('px', '')
        this.container.style.height                   = h + 'px';
        this.document.body.style.height               = h + 'px';
        this.document.head.parentElement.style.height = h + 'px';
      }
    }
  }

  realSize() {
    this._fakeSize = false;
    this.matchContent();
  }

  matchContent() {
    if (this.name == 'widget') return;

    if (this._isIframe && !this._fakeSize) {
      const oldWidth = this.container.style.width
      const oldHeight = this.container.style.height

      let forcedWidth = this.forcedWidth();
      this.document.body.style.width               = '';
      this.document.head.parentElement.style.width = '';
      this.container.style.width                   = Utils.exists(forcedWidth)
                                                      ? forcedWidth
                                                      : this.responsiveChildren.width
                                                        ? this.element.offsetWidth + "px"
                                                        : '';

      let forcedHeight = this.forcedHeight();
      this.document.body.style.height               = '';
      this.document.head.parentElement.style.height = '';
      this.container.style.height                   =  Utils.exists(forcedHeight)
                                                        ? forcedHeight
                                                        : this.responsiveChildren.height
                                                          ? this.element.offsetHeight + "px"
                                                          : '';

      if (oldWidth != this.container.style.width || oldHeight != this.container.style.height) {
        this._trigger('size_changed', {
          width: this.container.style.width,
          height: this.container.style.height
        })
      }
    }
  }

  destroy() {
    this._trigger('destroy');
    let container = Dom.parent.document.getElementById(this.id);
    if (container)
      container.parentElement.removeChild(container);
  }

  /**
   * container
   *
   * Use the container if you need to tweek the position in the parent document.
   *
   * IFRAME MODE
   * document
   *   head
   *   body
   *     div#slaask-element.slaask-component
   *       iframe.slaask-iframe                   <= this.container
   *         document
   *           head
   *           body
   *             div#slaask-element               <= this.element
   *
   * ORIGINAL MODE
   * document
   *   head
   *   body
   *     div#slaask-element.slaask-component      <= this.container == this.element
   *
   * @return {Element} Either the iframe, or the div element
   */
  get container() {
    return Dom.parent.document.getElementById(this.id);
  }

  get element() {
    return this._element;
  }

  get cssUrl() {
    return Dom.getCssUrl(this.name);
  }

  get window() {
    return this.document.defaultView;
  }

  get document() {
    return this._element.ownerDocument;
  }

  get id() {
    return "slaask-" + this.name;
  }

}
